import { useState } from 'react';
import { money, utcDate, daysUntil, relativeDays } from '../format';
import { EXPIRY_WARN_DAYS, keyState } from '../keyState';
import type { CustomerFlagIn, Identity, KeyGuardrailNameOut, KeyListOut, KeyOut, KeyUpdateIn } from '../types';
import { KeyActions } from './KeyActions';
import { KeySpendView } from './KeySpendView';
import { LimitCell } from './LimitCell';
import { Provenance } from './Provenance';
import { SpendCell } from './SpendCell';
import { StatusBadge, UnmanagedBadge } from './StatusBadge';

interface KeyListProps {
  list: KeyListOut;
  identity?: Identity | null;
  /** Assigned-guardrail names keyed by key id. null = still loading (show a placeholder);
   *  a Map with no entry for a key means that key has no guardrail. */
  guardrailNames?: Map<string, KeyGuardrailNameOut> | null;
  onUpdate?: (id: string, data: KeyUpdateIn) => Promise<void>;
  onDisable?: (id: string) => Promise<void>;
  onSetCustomer?: (id: string, data: CustomerFlagIn) => Promise<void>;
  onRequestIncrease?: (key: KeyOut) => void;
  onRequestGuardrail?: (key: KeyOut) => void;
  onSetupClaudeCode?: (key: KeyOut) => void;
  onChangeGuardrail?: (key: KeyOut) => void;
}

const HIDE_DISABLED_STORAGE = 'keys.hideDisabled';

/** The viewer's last choice, defaulting to hiding: a disabled key can never be re-enabled
 *  here, so it is mostly clutter. Storage can be unavailable (private windows). */
function readHideDisabled(): boolean {
  try {
    return localStorage.getItem(HIDE_DISABLED_STORAGE) !== 'false';
  } catch {
    return true;
  }
}

export function KeyList({ list, identity, guardrailNames, onUpdate, onDisable, onSetCustomer, onRequestIncrease, onRequestGuardrail, onSetupClaudeCode, onChangeGuardrail }: KeyListProps) {
  const [hideDisabled, setHideDisabled] = useState(readHideDisabled);

  if (list.items.length === 0) return <EmptyState list={list} />;

  const toggleHideDisabled = (hide: boolean) => {
    setHideDisabled(hide);
    try {
      localStorage.setItem(HIDE_DISABLED_STORAGE, String(hide));
    } catch {
      // A preference that does not persist is still applied for this visit.
    }
  };

  const disabledCount = list.items.filter((k) => k.disabled).length;
  const items = hideDisabled ? list.items.filter((k) => !k.disabled) : list.items;
  const unmanaged = items.filter((k) => !k.is_managed).length;
  const blocked = items.filter((k) => keyState(k).severity === 'blocked').length;

  return (
    <section aria-labelledby="keys-heading">
      <div className="section-head">
        <h2 id="keys-heading">Virtual keys</h2>
        <label className="hide-disabled small">
          <input
            type="checkbox"
            checked={hideDisabled}
            onChange={(e) => toggleHideDisabled(e.target.checked)}
          />
          Hide disabled keys{disabledCount > 0 ? ` (${disabledCount})` : ''}
        </label>
        <Provenance list={list} />
      </div>

      {unmanaged > 0 ? (
        <p className="callout callout-unmanaged" role="status">
          {unmanaged} {unmanaged === 1 ? 'key exists' : 'keys exist'} in an OpenRouter workspace with
          no owner recorded here. Ownership lives only in this app, so these need to be claimed or
          disabled by a platform admin.
        </p>
      ) : null}
      {blocked > 0 ? (
        <p className="callout callout-blocked" role="status">
          {blocked} {blocked === 1 ? 'key is' : 'keys are'} currently unable to serve requests.
        </p>
      ) : null}

      <table className="keys">
        <caption className="sr-only">
          Virtual keys with live current-period spend, limits and status
        </caption>
        <thead>
          <tr>
            <th scope="col">Key</th>
            <th scope="col">Team / owner</th>
            <th scope="col">Status</th>
            <th scope="col">
              Guardrail <span className="th-note">assigned policy</span>
            </th>
            <th scope="col">
              Limit <span className="th-note">current period (live)</span>
            </th>
            <th scope="col">
              Platform spend <span className="th-note">current period (live)</span>
            </th>
            <th scope="col">
              Lifetime <span className="th-note">all time, not comparable</span>
            </th>
            <th scope="col">Expires</th>
            {onUpdate && onDisable ? <th scope="col">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={onUpdate && onDisable ? 9 : 8} className="muted">
                All {disabledCount} {disabledCount === 1 ? 'key is' : 'keys are'} disabled. Untick
                &ldquo;Hide disabled keys&rdquo; to see them.
              </td>
            </tr>
          ) : null}
          {items.map((k) => (
            <KeyRow
              key={k.id}
              keyRow={k}
              identity={identity}
              guardrail={guardrailNames === undefined ? undefined : (guardrailNames?.get(k.id) ?? null)}
              guardrailsLoading={guardrailNames === null}
              onUpdate={onUpdate}
              onDisable={onDisable}
              onSetCustomer={onSetCustomer}
              onRequestIncrease={onRequestIncrease}
              onRequestGuardrail={onRequestGuardrail}
              onSetupClaudeCode={onSetupClaudeCode}
              onChangeGuardrail={onChangeGuardrail}
            />
          ))}
        </tbody>
      </table>

      <p className="footnote">
        Period spend and lifetime spend are different measures and are never added together.
        Limit bars are approximate: OpenRouter enforces limits in a separate pipeline.
      </p>
    </section>
  );
}

interface KeyRowProps {
  keyRow: KeyOut;
  identity: Identity | null | undefined;
  /** The key's assigned guardrail, or null if it has none. undefined = column not in use. */
  guardrail: KeyGuardrailNameOut | null | undefined;
  guardrailsLoading: boolean;
  onUpdate: ((id: string, data: KeyUpdateIn) => Promise<void>) | undefined;
  onDisable: ((id: string) => Promise<void>) | undefined;
  onSetCustomer: ((id: string, data: CustomerFlagIn) => Promise<void>) | undefined;
  onRequestIncrease: ((key: KeyOut) => void) | undefined;
  onRequestGuardrail: ((key: KeyOut) => void) | undefined;
  onSetupClaudeCode: ((key: KeyOut) => void) | undefined;
  onChangeGuardrail: ((key: KeyOut) => void) | undefined;
}

function KeyRow({ keyRow, identity, guardrail, guardrailsLoading, onUpdate, onDisable, onSetCustomer, onRequestIncrease, onRequestGuardrail, onSetupClaudeCode, onChangeGuardrail }: KeyRowProps) {
  const state = keyState(keyRow);
  const managed = keyRow.is_managed;
  const [showSpend, setShowSpend] = useState(false);
  return (
    <>
      <tr className={`${managed ? '' : 'row-unmanaged '}row-${state.severity}`}>
      <td>
        <div className="key-name">{keyRow.name}</div>
        {/* label is a masked preview from OpenRouter, not the key and not copyable. */}
        <div className="key-label">
          <span className="mono">{keyRow.label ?? 'no preview'}</span>
          <span className="muted"> masked preview, not the key</span>
        </div>
      </td>
      <td>
        {managed ? (
          <>
            <div>{keyRow.team_name ?? <span className="muted">no team</span>}</div>
            <div className="muted small">{keyRow.owner_email ?? 'no owner recorded'}</div>
          </>
        ) : (
          <div className="unmanaged-cell">
            <UnmanagedBadge />
            <div className="muted small">owner unknown - found upstream</div>
          </div>
        )}
      </td>
      <td>
        <StatusBadge state={state} />
        {state.issues.length > 0 ? (
          <ul className="issues">
            {state.issues.map((i) => (
              <li key={i.kind} className={`issue issue-${i.severity}`}>
                {i.detail}
              </li>
            ))}
          </ul>
        ) : null}
      </td>
      <td>
        <GuardrailCell
          managed={managed}
          guardrail={guardrail}
          loading={guardrailsLoading}
        />
      </td>
      <td>
        <LimitCell keyRow={keyRow} />
      </td>
      <td>
        <SpendCell
          daily={keyRow.usage_daily}
          weekly={keyRow.usage_weekly}
          monthly={keyRow.usage_monthly}
        />
      </td>
      <td className="numeric">
        <div title="Lifetime spend since the key was created. Not part of the current period.">
          {money(keyRow.usage, 'not reported')}
        </div>
        {keyRow.byok_usage !== null ? (
          <div className="small muted">+{money(keyRow.byok_usage)} BYOK</div>
        ) : null}
      </td>
      <td>
        <ExpiryCell expiresAt={keyRow.expires_at} />
      </td>
      {onUpdate && onDisable ? (
        <td>
          <KeyActions
            keyRow={keyRow}
            identity={identity ?? null}
            onUpdate={onUpdate}
            onDisable={onDisable}
            onSetCustomer={onSetCustomer}
            onRequestIncrease={onRequestIncrease}
            onRequestGuardrail={onRequestGuardrail}
            onSetupClaudeCode={onSetupClaudeCode}
            onChangeGuardrail={onChangeGuardrail}
          />
          {managed && (
            <button
              onClick={() => setShowSpend(!showSpend)}
              style={{ marginTop: '4px', fontSize: '12px' }}
            >
              {showSpend ? 'Hide' : 'View'} spend history
            </button>
          )}
        </td>
      ) : null}
    </tr>
    {showSpend && managed && (
      <tr className="key-spend-row">
        <td colSpan={9}>
          <KeySpendView keyId={keyRow.id} />
        </td>
      </tr>
    )}
    </>
  );
}

function GuardrailCell({
  managed,
  guardrail,
  loading,
}: {
  managed: boolean;
  guardrail: KeyGuardrailNameOut | null | undefined;
  loading: boolean;
}) {
  // Unmanaged keys have no guardrail flow, and the lookup only covers managed keys.
  if (!managed) return <span className="muted">—</span>;
  if (loading) return <span className="muted small">loading…</span>;
  // No per-key assignment means the workspace's default guardrail applies, so this reads
  // "Default" rather than "none": the key is still covered, just not by a specific policy.
  if (!guardrail || !guardrail.guardrail_id)
    return <span className="muted" title="No specific guardrail; the workspace default applies">Default</span>;
  // A resolved name is friendliest; fall back to the id if the name could not be fetched.
  return <span title={guardrail.guardrail_id}>{guardrail.guardrail_name ?? guardrail.guardrail_id}</span>;
}

function ExpiryCell({ expiresAt }: { expiresAt: string | null }) {
  const days = daysUntil(expiresAt);
  if (days === null) {
    return (
      <span className="muted" title="No expiry recorded. Policy requires a 90-day expiry.">
        none
      </span>
    );
  }
  const cls = days < 0 ? 'expiry expired' : days <= EXPIRY_WARN_DAYS ? 'expiry soon' : 'expiry';
  return (
    <div className={cls}>
      <div>{utcDate(expiresAt)}</div>
      <div className="small">{relativeDays(days)}</div>
    </div>
  );
}

function EmptyState({ list }: { list: KeyListOut }) {
  return (
    <section aria-labelledby="keys-heading">
      <div className="section-head">
        <h2 id="keys-heading">Virtual keys</h2>
        <Provenance list={list} />
      </div>
      <div className="empty">
        <h3>No keys yet</h3>
        <p>
          Nothing is visible to you in OpenRouter. Members see their own keys and leads see their
          team's, so an empty list can also mean you are not a member of a team with keys yet.
        </p>
      </div>
    </section>
  );
}
